import { ApexOptions } from 'apexcharts';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useIntl } from 'react-intl';

import { ChartRendererProps } from '../../types/chart-renderer-props';
import { ChartSize } from '../../types/chart-renderer-props';
import { HeatmapCellClickPayload, resolveHeatmapCellClick } from '../../utils/resolve-heatmap-cell-click';
import {
    WEEKDAY_WEEK_HEATMAP_DAYS,
    WEEKDAY_WEEK_HEATMAP_WEEKS,
    WeekdayWeekHeatmapModel,
    WeekdayWeekHeatmapSeries,
    deriveWeekdayWeekHeatmapRangeStart,
    toWeekdayWeekHeatmapModel
} from '../../utils/to-weekday-week-heatmap-series';
import { ChartContainer } from '../shared/chart-container.component';
import { getWeekdayWeekHeatmapChartOptions } from './weekday-week-heatmap-chart-options';

export interface WeekdayWeekHeatmapChartProps extends ChartRendererProps {
    rangeStart?: string;
    weekCount?: number;
    /** When set, non-empty cells are clickable and invoke this with cell date/value. */
    onCellClick?: (cell: HeatmapCellClickPayload) => void;
}

/** Apex heatmaps are stable with category x-axis + plain numeric y arrays (not { x, y } objects). */
export const toApexHeatmapSeries = (series: WeekdayWeekHeatmapSeries[]): ApexOptions['series'] =>
    series.map(({ name, data }) => ({
        name: name?.trim() || 'Day',
        data: data.map(({ y }) => y)
    }));

interface WeekdayWeekHeatmapChartViewProps {
    heatmapModel: WeekdayWeekHeatmapModel;
    apexSeries: ApexOptions['series'];
    size: ChartSize;
    chartOptions?: ApexOptions;
    onCellClick?: (cell: HeatmapCellClickPayload) => void;
}

const WeekdayWeekHeatmapChartView = ({ heatmapModel, apexSeries, size, chartOptions, onCellClick }: WeekdayWeekHeatmapChartViewProps) => {
    const options = useMemo(() => {
        const base = getWeekdayWeekHeatmapChartOptions({
            height: size.height,
            width: size.width,
            maxValue: heatmapModel.maxValue,
            weekLabels: heatmapModel.weekLabels,
            tooltipSeries: heatmapModel.series
        });

        if (!onCellClick) {
            return { ...base, ...chartOptions };
        }

        return {
            ...base,
            ...chartOptions,
            chart: {
                ...base.chart,
                ...chartOptions?.chart,
                events: {
                    ...base.chart?.events,
                    ...chartOptions?.chart?.events,
                    dataPointSelection: (event, chartContext, config) => {
                        event?.preventDefault?.();
                        event?.stopPropagation?.();
                        chartOptions?.chart?.events?.dataPointSelection?.(event, chartContext, config);
                        const cell = resolveHeatmapCellClick(heatmapModel.series, config.seriesIndex, config.dataPointIndex);
                        if (cell) {
                            onCellClick(cell);
                        }
                    }
                }
            }
        };
    }, [apexSeries, chartOptions, heatmapModel.maxValue, heatmapModel.series, heatmapModel.weekLabels, onCellClick, size.height, size.width]);

    return (
        <ReactApexChart
            key={`${heatmapModel.maxValue}-${heatmapModel.weekLabels.length}`}
            options={options}
            series={apexSeries}
            type='heatmap'
            height={size.height}
            width={size.width}
        />
    );
};

export const WeekdayWeekHeatmapChart = ({
    data,
    className,
    minHeight = 200,
    chartOptions,
    rangeStart,
    weekCount = WEEKDAY_WEEK_HEATMAP_WEEKS,
    onCellClick
}: WeekdayWeekHeatmapChartProps) => {
    const intl = useIntl();

    const dayLabels = useMemo(
        () =>
            Array.from({ length: WEEKDAY_WEEK_HEATMAP_DAYS }, (_, index) => {
                const date = new Date(Date.UTC(2024, 0, 1 + index));
                return intl.formatDate(date, { weekday: 'short', timeZone: 'UTC' }) || `Day ${index + 1}`;
            }),
        [intl]
    );

    const heatmapModel = useMemo(() => {
        const parsedRangeStart = rangeStart ? new Date(rangeStart) : deriveWeekdayWeekHeatmapRangeStart(data);
        if (!parsedRangeStart || Number.isNaN(parsedRangeStart.getTime())) {
            return null;
        }

        return toWeekdayWeekHeatmapModel(data, parsedRangeStart, weekCount, dayLabels);
    }, [data, dayLabels, rangeStart, weekCount]);

    const apexSeries = useMemo(() => (heatmapModel ? toApexHeatmapSeries(heatmapModel.series) : []), [heatmapModel]);

    if (!heatmapModel || !apexSeries?.length) {
        return null;
    }

    const containerClassName = `${className ?? ''}${onCellClick ? ' cursor-pointer' : ''}`.trim();

    return (
        <ChartContainer
            className={containerClassName || undefined}
            minHeight={minHeight}>
            {(size) => (
                <WeekdayWeekHeatmapChartView
                    heatmapModel={heatmapModel}
                    apexSeries={apexSeries}
                    size={size}
                    chartOptions={chartOptions}
                    onCellClick={onCellClick}
                />
            )}
        </ChartContainer>
    );
};
